// src/components/ui/datepicker.jsx
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays } from "lucide-react";

function formatDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("pt-BR");
}

export default function DatePicker({ value, onChange, placeholder = "Selecione uma data" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Estado interno sincronizado com a prop `value`
  const [selectedDate, setSelectedDate] = useState(
    value ? new Date(value) : null
  );
  const [currentMonth, setCurrentMonth] = useState(
    value ? new Date(value) : new Date()
  );
  const [direction, setDirection] = useState(0);

  // Sempre que `value` mudar fora, sincroniza o estado interno
  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value));
      setCurrentMonth(new Date(value));
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  // Fecha ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Gera semanas do mês
  const generateWeeks = (month) => {
    const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const endOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const daysInMonth = endOfMonth.getDate();
    const startDay = startOfMonth.getDay();

    const weeks = [];
    let day = 1 - startDay;

    while (day <= daysInMonth) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        if (day > 0 && day <= daysInMonth) {
          week.push(new Date(month.getFullYear(), month.getMonth(), day));
        } else {
          week.push(null);
        }
        day++;
      }
      weeks.push(week);
    }
    return weeks;
  };

  const weeks = generateWeeks(currentMonth);

  const handleSelect = (date) => {
    setSelectedDate(date);
    onChange?.(date.toISOString().split("T")[0]); // envia yyyy-mm-dd
    setOpen(false);
  };

  // Animações
  const variants = {
    enter: (direction) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction > 0 ? -50 : 50, opacity: 0 }),
  };

  return (
    <div ref={ref} className="relative w-full">
      {/* Input fake */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between border-0 border-b border-gray-400 
                   focus:border-b-2 focus:border-[#1d7f14] focus:ring-0 text-sm px-0 py-1.5 text-gray-700"
      >
        <span>{selectedDate ? formatDate(selectedDate) : placeholder}</span>
        <CalendarDays size={16} className="text-gray-500 ml-1" />
      </button>

      {/* Dropdown calendário */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className="absolute left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-md z-50 p-3 w-60"
          >
            {/* Cabeçalho */}
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => {
                  setDirection(-1);
                  setCurrentMonth(
                    new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
                  );
                }}
                className="px-2 text-gray-500 hover:text-gray-800 text-sm"
              >
                {"<"}
              </button>
              <span className="text-sm font-medium text-gray-700 capitalize">
                {currentMonth.toLocaleDateString("pt-BR", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={() => {
                  setDirection(1);
                  setCurrentMonth(
                    new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
                  );
                }}
                className="px-2 text-gray-500 hover:text-gray-800 text-sm"
              >
                {">"}
              </button>
            </div>

            {/* Corpo */}
            <div className="overflow-hidden w-full">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentMonth.getMonth() + "-" + currentMonth.getFullYear()}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  custom={direction}
                  transition={{ duration: 0.25 }}
                >
                  {/* Dias da semana */}
                  <div className="grid grid-cols-7 text-[12px] font-medium text-gray-500 mb-1">
                    {["D", "S", "T", "Q", "Q", "S", "S"].map((d, i) => (
                      <div key={i} className="text-center">
                        {d}
                      </div>
                    ))}
                  </div>

                  {/* Dias */}
                  <div className="grid grid-cols-7 gap-1 text-sm">
                    {weeks.map((week, wi) =>
                      week.map((d, di) =>
                        d ? (
                          <button
                            key={di}
                            onClick={() => handleSelect(d)}
                            className={`w-8 h-8 flex items-center justify-center rounded-full 
                              ${
                                selectedDate?.toDateString() === d.toDateString()
                                  ? "bg-[#1d7f14] text-white"
                                  : "hover:bg-gray-100 text-gray-700"
                              }`}
                          >
                            {d.getDate()}
                          </button>
                        ) : (
                          <div key={di} className="w-8 h-8"></div>
                        )
                      )
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
